import { MOCK_PERFORMANCE_SUMMARY, generateMockTrainingHistory } from '../pages/model/mockModelData';

// 환경에 따라 자동으로 API URL 결정
export const getApiBaseUrl = () => {
  // 환경 변수가 설정되어 있으면 우선 사용
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // 프로덕션 환경 - 모든 테넌트 도메인이 같은 API 서버 사용
  const prodHosts = ['smi.seoro.ai.kr', 'smi-vn.seoro.ai.kr', 'smi-kr.seoro.ai.kr'];
  if (prodHosts.includes(window.location.hostname)) {
    return 'https://smi-api.seoro.ai.kr/api';
  }

  // 로컬 개발 환경
  return 'http://localhost:8080/api';
};

// AI 서비스 직접 호출 URL (로컬 테스트용)
export const getAIServiceUrl = () => {
  if (process.env.REACT_APP_AI_SERVICE_URL) {
    return process.env.REACT_APP_AI_SERVICE_URL;
  }
  // 로컬 Python AI 서비스
  return 'http://localhost:8001/api/llm';
};

// 백엔드 서버 기본 URL (API 경로 제외)
export const getBackendBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    // /api 제거
    return process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '');
  }

  const prodHosts2 = ['smi.seoro.ai.kr', 'smi-vn.seoro.ai.kr', 'smi-kr.seoro.ai.kr'];
  if (prodHosts2.includes(window.location.hostname)) {
    return 'https://smi-api.seoro.ai.kr';
  }

  return 'http://localhost:8080';
};

// 이미지 URL을 전체 URL로 변환
export const getFullImageUrl = (path) => {
  if (!path) return '';
  // 이미 전체 URL이면 그대로 반환
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  // 상대 경로면 백엔드 URL 추가
  return getBackendBaseUrl() + path;
};

const API_BASE_URL = getApiBaseUrl();

// 사용자 정보 캐시 (성능 최적화)
let cachedUser = null;
let cachedSchemaName = null;

// 사용자 정보에서 스키마명 가져오기 (캐싱 적용)
export const getSchemaName = () => {
  const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');

  // 캐시된 값이 있고 스토리지 값이 동일하면 캐시 반환
  if (cachedUser === userStr && cachedSchemaName) {
    return cachedSchemaName;
  }

  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      cachedUser = userStr;
      cachedSchemaName = user.schemaName || 'seoro';
      return cachedSchemaName;
    } catch (e) {
      console.error('Failed to parse user data:', e);
      return 'seoro';
    }
  }
  return 'seoro'; // 로그인 전 기본값
};

// CSRF 토큰 관리
let csrfToken = null;

const getCsrfToken = () => {
  if (csrfToken) return csrfToken;

  // 메타 태그에서 CSRF 토큰 읽기
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    csrfToken = metaTag.getAttribute('content');
    return csrfToken;
  }

  // 쿠키에서 CSRF 토큰 읽기 (Spring Security 기본)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      csrfToken = decodeURIComponent(value);
      return csrfToken;
    }
  }

  return null;
};

// 공통 헤더 생성 함수 (JWT 토큰 + CSRF 토큰 포함)
export const getHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const schemaName = getSchemaName();
  const csrf = getCsrfToken();

  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Tenant-ID': schemaName,
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(csrf && { 'X-XSRF-TOKEN': csrf })
  };
};

// ============================================
// 공통 API 요청 함수 (중복 코드 제거)
// ============================================

/**
 * 공통 API 요청 함수
 * @param {string} url - API URL
 * @param {object} options - fetch 옵션 (method, body 등)
 * @param {string} errorContext - 에러 로그용 컨텍스트
 * @param {object} config - 추가 설정 { checkSuccess: true, returnData: true }
 */
const apiRequest = async (url, options = {}, errorContext = 'API request', config = {}) => {
  const { checkSuccess = true, returnData = true } = config;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      ...options
    });

    if (!response.ok) {
      // 토큰 만료/권한 없음 → 로그인 페이지로 이동
      if ((response.status === 401 || response.status === 403) && window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // DELETE 요청은 본문이 없을 수 있음
    if (options.method === 'DELETE' && response.status === 204) {
      return { success: true };
    }

    const result = await response.json();

    // success 필드 체크가 필요한 경우
    if (checkSuccess && result.success === false) {
      throw new Error(result.message || 'API returned success: false');
    }

    // data 필드만 반환할지 전체 반환할지
    return returnData && result.data !== undefined ? result.data : result;
  } catch (error) {
    console.error(`Failed to ${errorContext}:`, error);
    throw error;
  }
};

/**
 * GET 요청 헬퍼
 */
const apiGet = (url, errorContext, config) =>
  apiRequest(url, { method: 'GET' }, errorContext, config);

/**
 * POST 요청 헬퍼
 */
const apiPost = (url, body, errorContext, config) =>
  apiRequest(url, { method: 'POST', body: JSON.stringify(body) }, errorContext, config);

/**
 * PUT 요청 헬퍼
 */
const apiPut = (url, body, errorContext, config) =>
  apiRequest(url, { method: 'PUT', body: JSON.stringify(body) }, errorContext, config);

/**
 * DELETE 요청 헬퍼
 */
const apiDelete = (url, errorContext) =>
  apiRequest(url, { method: 'DELETE' }, errorContext, { checkSuccess: false, returnData: false });

// ============================================
// Dashboard API
// ============================================

// Equipment 목록 조회
export const fetchEquipments = () =>
  apiGet(`${API_BASE_URL}/dashboard/equipments`, 'fetch equipments');

// Machine Overview 데이터 조회
export const fetchMachineOverview = (equipmentId) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/overview`, `fetch machine overview (equipmentId: ${equipmentId})`);

// Machine Cycle 데이터 조회
export const fetchMachineCycle = (equipmentId) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/cycle`, `fetch machine cycle (equipmentId: ${equipmentId})`);

// Machine Temperature 데이터 조회
export const fetchMachineTemperature = (equipmentId) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/temperature`, `fetch machine temperature (equipmentId: ${equipmentId})`);

// Machine Drive 데이터 조회
export const fetchMachineDrive = (equipmentId) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/drive`, `fetch machine drive (equipmentId: ${equipmentId})`);

// Machine 감시(감시1) 데이터 조회 — 현재 샷 + 전쇼트 + 자동샘플링용 최근 샷(sampleSize, 현재 샷 제외)
export const fetchMachineWatch1 = (equipmentId, sampleSize = 0) =>
  apiGet(`${API_BASE_URL}/machine/watch1/${equipmentId}?sampleSize=${sampleSize}`, `fetch machine watch1 (equipmentId: ${equipmentId})`);

// Machine 감시2 · 샷로그 데이터 조회 — 최근 limit 샷 (기본 100, 최대 1000)
export const fetchMachineWatch2 = (equipmentId, limit = 100) =>
  apiGet(`${API_BASE_URL}/machine/watch2/${equipmentId}?limit=${limit}`, `fetch machine watch2 (equipmentId: ${equipmentId})`);

// Machine PPH(시간당 생산량) 조회 — date(yyyy-MM-dd) 미지정 시 오늘
export const fetchMachinePph = (equipmentId, date) =>
  apiGet(`${API_BASE_URL}/machine/pph/${equipmentId}${date ? `?date=${date}` : ''}`, `fetch machine pph (equipmentId: ${equipmentId})`);

// Machine 하루 샷 양/불 집계 — 품질·생산 > 공정관리 "생산 요약". date 미지정 시 오늘
export const fetchMachineQualitySummary = (equipmentId, date) =>
  apiGet(`${API_BASE_URL}/machine/quality-summary/${equipmentId}${date ? `?date=${date}` : ''}`, `fetch machine quality summary (equipmentId: ${equipmentId})`);

// Machine 최근 N일 일별 생산(샷) 집계 — 품질·생산 > 생산 "생산량 추이" (오늘 포함, 샷 없는 날은 0)
export const fetchDailyProduction = (equipmentId, days = 14) =>
  apiGet(`${API_BASE_URL}/machine/daily-production/${equipmentId}?days=${days}`, `fetch daily production (equipmentId: ${equipmentId})`);

// Machine 하루 비전검사 결과 — 품질·생산 > 비전검사. 요약(유형별 NG 건수) + 필터·페이지 목록. date 미지정 시 오늘
export const fetchVisionInspection = (equipmentId, { date, verdict, defectType, page = 0, size = 9 } = {}) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (verdict) params.set('verdict', verdict);
  if (defectType) params.set('defectType', defectType);
  params.set('page', String(page));
  params.set('size', String(size));
  return apiGet(`${API_BASE_URL}/machine/vision-inspection/${equipmentId}?${params.toString()}`, `fetch vision inspection (equipmentId: ${equipmentId})`);
};

// Machine 하루 융합검사 결과 — 품질·생산 > 융합검사. 가중치 테이블·2×2 일치표·샷별 융합 판정. caseFilter: disagree/changed/rescued/suppressed/POK_VOK…
export const fetchFusionInspection = (equipmentId, { date, verdict, caseFilter, page = 0, size = 15 } = {}) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (verdict) params.set('verdict', verdict);
  if (caseFilter) params.set('case', caseFilter);
  params.set('page', String(page));
  params.set('size', String(size));
  return apiGet(`${API_BASE_URL}/machine/fusion-inspection/${equipmentId}?${params.toString()}`, `fetch fusion inspection (equipmentId: ${equipmentId})`);
};

// Machine 하루 샷동기화 결과 — 품질·생산 > 샷동기화. 스테이션·엔진 상태, 요약, 10분 추이, 짝 목록. status: MATCH/CHECK/UNMATCHED
export const fetchShotSync = (equipmentId, { date, status, page = 0, size = 15 } = {}) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (status) params.set('status', status);
  params.set('page', String(page));
  params.set('size', String(size));
  return apiGet(`${API_BASE_URL}/machine/shot-sync/${equipmentId}?${params.toString()}`, `fetch shot sync (equipmentId: ${equipmentId})`);
};

// 샷동기화 실시간 타임라인 — 최근 N분 공정 샷·이미지(보정 시각)와 짝
export const fetchShotSyncTimeline = (equipmentId, minutes = 3) =>
  apiGet(`${API_BASE_URL}/machine/shot-sync/${equipmentId}/timeline?minutes=${minutes}`, `fetch shot sync timeline (equipmentId: ${equipmentId})`);

// 설비의 샷동기화 스테이션 (데모 공정·데모 카메라 여부). 스테이션이 없으면 null
export const fetchShotSyncStation = (equipmentId) =>
  apiGet(`${API_BASE_URL}/machine/shot-sync/${equipmentId}/station`, `fetch shot sync station (equipmentId: ${equipmentId})`);

// LSTM 불량 원인 분석 (AI 서비스 프록시) — feature_importance / recommendations / confidence. 품질·생산 > 공정검사 인라인 패널
export const fetchLstmDefectAnalysis = (equipmentId, craftId, shotNumber) =>
  apiGet(`${API_BASE_URL}/lstm/analyze/${equipmentId}/${encodeURIComponent(craftId)}${shotNumber != null ? `?shot_number=${shotNumber}` : ''}`, `fetch LSTM defect analysis (equipmentId: ${equipmentId}, craftId: ${craftId})`);

// Machine Alarm 데이터 조회
export const fetchMachineAlarm = (equipmentId) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/alarm`, `fetch machine alarm (equipmentId: ${equipmentId})`);

// 샷 이력 조회 (최근 N개, 판정·기록시각 포함)
export const fetchShotHistory = (equipmentId, limit = 100) =>
  apiGet(`${API_BASE_URL}/machine/shot-history/${equipmentId}?limit=${limit}`, `fetch shot history (equipmentId: ${equipmentId}, limit: ${limit})`);

// Machine SPC 데이터 조회
export const fetchMachineSpc = (equipmentId, parameter = 'cycle_time') =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/spc?parameter=${parameter}`, `fetch machine SPC (equipmentId: ${equipmentId}, parameter: ${parameter})`);

// 샷 원시 데이터 조회
export const fetchShotRawData = (equipmentId, limit = 100) =>
  apiGet(`${API_BASE_URL}/machine/raw-data/${equipmentId}?limit=${limit}`, `fetch shot raw data (equipmentId: ${equipmentId})`);

// Factory 목록 조회
export const fetchFactories = () =>
  apiGet(`${API_BASE_URL}/dashboard/factories`, 'fetch factories');

// Company Dashboard 데이터 조회
export const fetchCompanyDashboard = () =>
  apiGet(`${API_BASE_URL}/dashboard/company`, 'fetch company dashboard');

// Company Factory Overview 데이터 조회
export const fetchCompanyFactoryOverview = () =>
  apiGet(`${API_BASE_URL}/dashboard/company-factory`, 'fetch company factory overview');

// Factory Dashboard 데이터 조회
export const fetchFactoryDashboard = (factoryId) =>
  apiGet(`${API_BASE_URL}/dashboard/factory/${factoryId}`, `fetch factory dashboard (factoryId: ${factoryId})`);

// 데이터 수집 흐름 스냅샷 (설비별 샷 도착 통계·ETL 동기화·최근 알람). 화면이 5초 주기로 폴링
export const fetchPipelineSnapshot = () =>
  apiGet(`${API_BASE_URL}/dashboard/pipeline/snapshot`, 'fetch pipeline snapshot');

// Machine Dashboard 데이터 조회 (공장 전체 또는 라인별)
export const fetchMachineDashboard = (factoryId, lineId = null) => {
  const url = lineId
    ? `${API_BASE_URL}/dashboard/machines/${factoryId}/${lineId}`
    : `${API_BASE_URL}/dashboard/machines/${factoryId}`;
  return apiGet(url, `fetch machine dashboard (factoryId: ${factoryId}, lineId: ${lineId})`);
};

// 공장 불량 이력 — 공장 모니터링 > 불량 이력. source: fusion|vision|process, from/to: 'YYYY-MM-DDTHH:mm:ss'(KST)
export const fetchFactoryDefectHistory = (factoryId, { from, to, source = 'fusion', equipmentId, type, page = 0, size = 20 } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  params.set('source', source);
  if (equipmentId) params.set('equipmentId', String(equipmentId));
  if (type) params.set('type', type);
  params.set('page', String(page));
  params.set('size', String(size));
  return apiGet(`${API_BASE_URL}/dashboard/factory/${factoryId}/defect-history?${params.toString()}`, `fetch factory defect history (factoryId: ${factoryId})`);
};

// 공장 라인 목록 조회
export const fetchFactoryLines = (factoryId) =>
  apiGet(`${API_BASE_URL}/factory/${factoryId}/lines`, `fetch factory lines (factoryId: ${factoryId})`);

// ============================================
// Widget API
// ============================================

// Widget Config 조회 (사용자 기준)
export const fetchWidgetConfig = (userId) =>
  apiGet(`${API_BASE_URL}/dashboard/widget-config?userId=${userId}`, `fetch widget config (userId: ${userId})`);

// Widget Config 저장 (사용자 기준)
export const saveWidgetConfig = (userId, widgets) => {
  const requestBody = {
    widgets: widgets.map(widget => ({
      widgetType: widget.type,
      widgetSize: widget.size,
      slotNumber: widget.slot,
      customConfig: widget.type === '사용자모듈' ? widget.config : null
    }))
  };
  return apiPost(`${API_BASE_URL}/dashboard/widget-config?userId=${userId}`, requestBody, `save widget config (userId: ${userId})`);
};

// Widget 개별 추가 (사용자 기준)
export const addWidget = (userId, widget) => {
  const requestBody = {
    widgetType: widget.type,
    widgetSize: widget.size,
    slotNumber: widget.slot,
    customConfig: widget.type === '사용자모듈' ? widget.config : null
  };
  return apiPost(`${API_BASE_URL}/dashboard/widget-config/add?userId=${userId}`, requestBody, `add widget (userId: ${userId})`);
};

// Machine Widget 데이터 조회
export const fetchMachineWidget = (equipmentId, widgetType) => {
  const endpointMap = {
    '온도': 'temperature',
    '압력': 'pressure',
    '에너지': 'energy',
    '기본정보': 'basic-info',
    '기본사출정보': 'injection-info',
    '알람이력': 'alarm-history'
  };

  const endpoint = endpointMap[widgetType];
  if (!endpoint) {
    return Promise.reject(new Error(`Unknown widget type: ${widgetType}`));
  }

  return apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/widget/${endpoint}`, `fetch machine widget (equipmentId: ${equipmentId}, type: ${widgetType})`);
};

// Custom Widget 데이터 조회
// 에너지 위젯 요약 — 오늘 kWh(설비 현재 상태) · 오늘 샷 수 · 샷당 kWh · 최근 recent 샷 에너지
export const fetchEnergySummary = (equipmentId, recent = 7) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/widget/energy-summary?recent=${recent}`, `fetch energy summary (equipmentId: ${equipmentId})`);

// 사용자 모듈 위젯 데이터 — 위젯 ID 없이 y축 종류(pressure | temperature | speed | position)로 조회.
// 위젯 설정 저장이 행을 다시 만들어 id 가 바뀌어도 영향을 받지 않는다
export const fetchCustomWidgetData = (equipmentId, yAxis) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/widget/custom-data?yAxis=${encodeURIComponent(yAxis || 'temperature')}`, `fetch custom widget data (equipmentId: ${equipmentId}, yAxis: ${yAxis})`);

export const fetchCustomWidget = (equipmentId, widgetId) =>
  apiGet(`${API_BASE_URL}/dashboard/machine/${equipmentId}/widget/custom/${widgetId}`, `fetch custom widget (equipmentId: ${equipmentId}, widgetId: ${widgetId})`);

// ============================================
// Factory Management API (공장/설비관리)
// ============================================

// Factory Management API (공장 목록 - 환경데이터 포함)
export const fetchFactoriesForManagement = async (page = 0, size = 1000) => {
  const data = await apiGet(`${API_BASE_URL}/management/factories?page=${page}&size=${size}`, 'fetch factories for management');
  // PageResponse인 경우 content 배열 반환, 배열인 경우 그대로 반환
  return data?.content || data || [];
};

// Factory Detail API (공장 상세정보 - 환경데이터 포함)
export const fetchFactoryDetailForManagement = (factoryId) =>
  apiGet(`${API_BASE_URL}/management/factories/${factoryId}`, `fetch factory detail (factoryId: ${factoryId})`);

// Factory Create API (공장 등록)
export const createFactory = (factoryData) =>
  apiPost(`${API_BASE_URL}/management/factories`, factoryData, 'create factory');

// Factory Update API (공장 수정)
export const updateFactory = (factoryId, factoryData) =>
  apiPut(`${API_BASE_URL}/management/factories/${factoryId}`, factoryData, `update factory (factoryId: ${factoryId})`);

// Factory Delete API (공장 삭제)
export const deleteFactory = (factoryId) =>
  apiDelete(`${API_BASE_URL}/management/factories/${factoryId}`, `delete factory (factoryId: ${factoryId})`);

// ============================================
// Equipment Management API (설비관리)
// ============================================

// Equipment Management API (설비 목록)
export const fetchEquipmentsForManagement = async (page = 0, size = 1000) => {
  const data = await apiGet(`${API_BASE_URL}/management/equipments?page=${page}&size=${size}`, 'fetch equipments for management');
  // PageResponse인 경우 content 배열 반환, 배열인 경우 그대로 반환
  return data?.content || data || [];
};

// Equipment Detail API (설비 상세정보)
export const fetchEquipmentDetailForManagement = (equipmentId) =>
  apiGet(`${API_BASE_URL}/management/equipments/${equipmentId}`, `fetch equipment detail (equipmentId: ${equipmentId})`);

// Equipment Create API (설비 등록)
export const createEquipment = (equipmentData) =>
  apiPost(`${API_BASE_URL}/management/equipments`, equipmentData, 'create equipment');

// Equipment Update API (설비 수정)
export const updateEquipment = (equipmentId, equipmentData) =>
  apiPut(`${API_BASE_URL}/management/equipments/${equipmentId}`, equipmentData, `update equipment (equipmentId: ${equipmentId})`);

// Equipment Delete API (설비 삭제)
export const deleteEquipment = (equipmentId) =>
  apiDelete(`${API_BASE_URL}/management/equipments/${equipmentId}`, `delete equipment (equipmentId: ${equipmentId})`);

// ============================================
// User Management API (회원관리)
// ============================================

// User List API (회원 목록 조회)
export const fetchUsersForManagement = async (page = 0, size = 1000) => {
  const data = await apiGet(`${API_BASE_URL}/users?page=${page}&size=${size}`, 'fetch users for management');
  // PageResponse인 경우 content 배열 반환, 배열인 경우 그대로 반환
  return data?.content || data || [];
};

// User Detail API (회원 상세정보)
export const fetchUserDetailForManagement = (userId) =>
  apiGet(`${API_BASE_URL}/users/${userId}`, `fetch user detail (userId: ${userId})`);

// Role Groups API (권한 그룹 목록 조회)
export const fetchRoleGroups = async (page = 0, size = 1000) => {
  const data = await apiGet(`${API_BASE_URL}/users/role-groups?page=${page}&size=${size}`, 'fetch role groups');
  // PageResponse인 경우 content 배열 반환, 배열인 경우 그대로 반환
  return data?.content || data || [];
};

// User Create API (회원 등록)
export const createUser = (userData) =>
  apiPost(`${API_BASE_URL}/users`, userData, 'create user');

// User Update API (회원 수정)
export const updateUser = (userId, userData) =>
  apiPut(`${API_BASE_URL}/users/${userId}`, userData, `update user (userId: ${userId})`);

// User Delete API (회원 삭제)
export const deleteUser = (userId) =>
  apiDelete(`${API_BASE_URL}/users/${userId}`, `delete user (userId: ${userId})`);

// ============================================
// 권한 관리 API
// ============================================

// 전체 권한 목록 조회
export const fetchPermissions = () =>
  apiGet(`${API_BASE_URL}/users/permissions`, 'fetch permissions');

// 권한 그룹 등록 — POST /users/role-groups { name, code, description, permissionIds } (role.create)
export const createRoleGroup = (roleGroupData) =>
  apiPost(`${API_BASE_URL}/users/role-groups`, roleGroupData, 'create role group');

// 권한 그룹 수정
export const updateRoleGroup = (roleGroupId, roleGroupData) =>
  apiPut(`${API_BASE_URL}/users/role-groups/${roleGroupId}`, roleGroupData, `update role group (roleGroupId: ${roleGroupId})`);

// 권한 그룹 삭제
export const deleteRoleGroup = (roleGroupId) =>
  apiDelete(`${API_BASE_URL}/users/role-groups/${roleGroupId}`, `delete role group (roleGroupId: ${roleGroupId})`);

// 멀티 에이전트 스트리밍 API
export const sendAgentMessageStream = async (message, conversationHistory = [], onToken, onStatus, onComplete) => {
  try {
    const history = conversationHistory
      .filter(msg => msg.role && msg.content && typeof msg.content === 'string')
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));

    // 로컬 개발 환경에서는 Python AI 서비스 직접 호출 (Java 백엔드 프록시 우회)
    const useDirectAI = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const streamUrl = useDirectAI
      ? `${getAIServiceUrl()}/agent/stream-v2`  // Python AI 서비스 직접 호출 (Tool Use v2)
      : `${API_BASE_URL}/llm/agent/stream`;  // Java 백엔드 프록시

    console.log('[Streaming] URL:', streamUrl, '(direct:', useDirectAI, ')');

    // 직접 AI 서비스 호출 시에는 간단한 헤더만 사용
    const headers = useDirectAI
      ? { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }
      : { ...getHeaders(), 'Accept': 'text/event-stream' };

    const response = await fetch(streamUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        question: message,
        conversation_history: history
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            // 상태 업데이트 (의도 분석 중, SQL 생성 중 등)
            if (data.status && onStatus) {
              onStatus(data.status);
            }

            // 토큰 스트리밍
            if (data.token && onToken) {
              fullText += data.token;
              onToken(data.token, fullText);
            }

            // 결과 수신 (done 이벤트 전에 result 이벤트가 먼저 옴)
            if (data.result) {
              result = {
                intent: data.result.intent,
                natural_language: fullText || data.result.natural_language,
                blocks: data.result.blocks || [],
                sql_query: data.result.sql_query,
                success: data.result.success,
                error: data.result.error,
                metadata: data.result.metadata
              };
            }

            // 완료 (result가 없이 done만 오는 경우 대비)
            if (data.done && !result) {
              result = {
                intent: data.intent,
                natural_language: fullText || data.natural_language,
                blocks: data.blocks || [],
                sql_query: data.sql_query,
                success: data.success,
                error: data.error,
                metadata: data.metadata
              };
            }
          } catch (e) {
            // JSON 파싱 실패 무시
          }
        }
      }
    }

    if (onComplete && result) {
      onComplete(result);
    }

    return result;
  } catch (error) {
    console.error('Failed to send streaming agent message:', error);
    throw error;
  }
};

// 공장별 일일 목표 생산량 저장 — targets: [{ factoryId, targetProduction(null=삭제) }]. 응답 data = 저장 후 전사 목표 합 (factory.update 권한)
export const updateFactoryTargets = (targets) =>
  apiPut(`${API_BASE_URL}/dashboard/company/factory-targets`, { targets }, 'update factory targets');

// ============================================
// 공지사항 API
// ============================================

// 공지사항 조회 (헤더용)
export const fetchNotices = (limit = 10) =>
  apiGet(`${API_BASE_URL}/notices?limit=${limit}`, 'fetch notices');

// 전체 공지사항 목록 조회 (관리 페이지용)
export const fetchAllNotices = async (page = 0, size = 1000) => {
  const data = await apiGet(`${API_BASE_URL}/notices/all?page=${page}&size=${size}`, 'fetch all notices');
  // PageResponse인 경우 content 배열 반환, 배열인 경우 그대로 반환
  return data?.content || data || [];
};

// 공지사항 상세 조회
export const fetchNoticeDetail = (id) =>
  apiGet(`${API_BASE_URL}/notices/${id}`, `fetch notice detail (id: ${id})`);

// 공지사항 등록
export const createNotice = (data) =>
  apiPost(`${API_BASE_URL}/notices`, data, 'create notice');

// 공지사항 수정
export const updateNotice = (id, data) =>
  apiPut(`${API_BASE_URL}/notices/${id}`, data, `update notice (id: ${id})`);

// 공지사항 삭제
export const deleteNotice = (id) =>
  apiDelete(`${API_BASE_URL}/notices/${id}`, `delete notice (id: ${id})`);

// 전사 최근 알람 조회
export const fetchRecentAlarms = (limit = 100) =>
  apiGet(`${API_BASE_URL}/dashboard/alarms/recent?limit=${limit}`, 'fetch recent alarms');

// ============================================
// AI API (ETL 및 데이터 수집 관리)
// ============================================

// AI 서비스 API URL
export const getAiApiBaseUrl = () => {
  if (process.env.REACT_APP_AI_API_BASE_URL) {
    return process.env.REACT_APP_AI_API_BASE_URL;
  }

  const prodHosts = ['smi.seoro.ai.kr', 'smi-vn.seoro.ai.kr', 'smi-kr.seoro.ai.kr'];
  if (prodHosts.includes(window.location.hostname)) {
    return 'https://smi-ai.seoro.ai.kr/api';
  }

  return 'http://localhost:8001/api';
};

// AI 서비스 원본 URL(/api 제외) — 라벨링 이미지는 AI 서비스가 /anno 로 정적 서빙하므로
// 프론트 자기 origin이 아니라 이 origin을 붙여서 요청해야 한다.
export const getAiServiceOrigin = () => getAiApiBaseUrl().replace(/\/api\/?$/, '');

// AI API 전용 요청 함수 (인증 헤더 없음)
const aiApiRequest = async (url, options = {}, errorContext = 'AI API request') => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Failed to ${errorContext}:`, error);
    throw error;
  }
};

// ============================================
// 라벨링(비전 어노테이션) API — AI 서비스
// ============================================
export const fetchAnnoClasses = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/classes`, {}, 'fetch anno classes');

export const fetchAnnoStats = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/stats`, {}, 'fetch anno stats');

// 폴더 선택 드롭다운에 쓸 최상위 폴더명 + 폴더별 이미지 개수 목록 조회
export const fetchAnnoFolders = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/folders`, {}, 'fetch anno folders');

export const fetchAnnoImages = (status = 'all', limit = 500, offset = 0, q = '') =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/images?status=${status}&limit=${limit}&offset=${offset}&q=${encodeURIComponent(q)}`, {}, 'fetch anno images');

export const fetchAnnoImage = (id) =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/images/${id}`, {}, 'fetch anno image');

export const saveAnnoImage = (id, payload) =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/images/${id}`, {
    method: 'POST', body: JSON.stringify(payload)
  }, 'save anno image');

// exportFormat: 'segmentation'(YOLOv8/v11/YOLO26용 폴리곤) 또는 'detection'(RT-DETR용 바운딩박스)
// folderName 을 주면 그 최상위 폴더의 완료 이미지만, 비우면 전체 폴더를 내보낸다
export const exportAnnoDataset = (exportFormat, folderName = '') =>
  aiApiRequest(
    `${getAiApiBaseUrl()}/anno/export?export_format=${exportFormat}&folder_name=${encodeURIComponent(folderName)}`,
    { method: 'POST' }, 'export anno dataset');

// 지난 내보내기 결과 목록(시각, 포맷, 내보낸 이미지/폴리곤 개수, 저장 경로) 조회
export const fetchAnnoExportList = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/exports`, {}, 'fetch anno export list');

// 내보내기 취소: 지난 내보내기 결과 폴더(이미지 복사본·라벨)를 서버에서 지운다. 라벨링 DB 는 그대로 남는다.
export const deleteAnnoExport = (exportRunName) =>
  aiApiRequest(`${getAiApiBaseUrl()}/anno/exports/${encodeURIComponent(exportRunName)}`, { method: 'DELETE' }, 'delete anno export');

// ETL 상태 조회
export const fetchETLStatus = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/etl/status`, {}, 'fetch ETL status');

// ETL 수동 실행
export const triggerETLSync = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/etl/sync`, { method: 'POST' }, 'trigger ETL sync');

// DB 연결 상태 조회
export const fetchDBConnections = () =>
  aiApiRequest(`${getAiApiBaseUrl()}/etl/db/test-connections`, {}, 'fetch DB connections');

// ============================================
// GPU Training API (smi-gpu 서버)
// GPU 서버 직접 호출, 실패 시 Mock 데이터 fallback
// ============================================

export const getGpuApiBaseUrl = () => {
  if (process.env.REACT_APP_GPU_API_BASE_URL) {
    return process.env.REACT_APP_GPU_API_BASE_URL;
  }

  if (window.location.hostname === 'smi.seoro.ai.kr') {
    return 'https://smi-gpu.seoro.ai.kr/api/training';
  }

  return 'http://175.45.202.152:8002/api/training';
};

const gpuApiRequest = async (url, timeoutMs = 5000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export const fetchModelPerformanceSummary = async () => {
  try {
    const data = await gpuApiRequest(`${getGpuApiBaseUrl()}/performance-summary`);
    console.log('[GPU API] performance-summary 로드 성공');
    data._source = 'gpu';
    return data;
  } catch (error) {
    console.warn('[GPU API] 연결 실패, Mock 데이터 사용:', error.message);
    return { ...MOCK_PERFORMANCE_SUMMARY, _source: 'mock' };
  }
};

export const fetchTrainingHistory = async (modelId, modelType = 'lstm') => {
  try {
    const data = await gpuApiRequest(`${getGpuApiBaseUrl()}/training-history/${modelId}?model_type=${modelType}`);
    console.log('[GPU API] training-history 로드 성공:', modelId);
    return data;
  } catch (error) {
    console.warn('[GPU API] training-history 실패, Mock 생성:', modelId);
    return generateMockTrainingHistory(modelId, modelType);
  }
};

// ============================================
// System API
// ============================================

// ETL 동기화 이력 조회 (Monitoring DB에서 직접 조회)
export const fetchETLSyncHistory = (limit = 20) =>
  apiGet(`${API_BASE_URL}/system/etl-history?limit=${limit}`, 'fetch ETL sync history');

// 로그인 로그 조회
export const fetchLoginLogs = async (page = 0, size = 1000, eventType = null) => {
  let url = `${API_BASE_URL}/system/login-logs?page=${page}&size=${size}`;
  if (eventType) {
    url += `&eventType=${eventType}`;
  }
  const data = await apiGet(url, 'fetch login logs');
  // PageResponse인 경우 content 배열 반환, 배열인 경우 그대로 반환
  return data?.content || data || [];
};


// ============================================
// AI 관리 (AI 분석 > AI 관리) — AI 서비스 /api/ai-admin
//   모델 연결 등록·수정·삭제·테스트, 기능별 모델 배정, 호출 이력. 응답은 { success, data }.
//   인증 헤더(getHeaders)를 같이 보내 테넌트·사용자 ID 가 로그에 남게 한다.
// ============================================
const aiAdminRequest = async (path, options = {}, errorContext = 'AI admin request') => {
  const response = await fetch(`${getAiApiBaseUrl()}/ai-admin${path}`, {
    method: 'GET',
    ...options,
    headers: { ...getHeaders(), ...(options.headers || {}) }
  });
  let body = null;
  try { body = await response.json(); } catch (e) { body = null; }
  if (!response.ok) {
    const detail = body && (body.detail || body.message);
    console.error(`Failed to ${errorContext}:`, response.status, detail);
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }
  return body && body.data !== undefined ? body.data : body;
};

export const fetchAiAdminMeta = () => aiAdminRequest('/meta', {}, 'fetch AI admin meta');
export const fetchAiConnections = () => aiAdminRequest('/connections', {}, 'fetch AI connections');
export const createAiConnection = (data) =>
  aiAdminRequest('/connections', { method: 'POST', body: JSON.stringify(data) }, 'create AI connection');
export const updateAiConnection = (id, data) =>
  aiAdminRequest(`/connections/${id}`, { method: 'PUT', body: JSON.stringify(data) }, 'update AI connection');
export const deleteAiConnection = (id) =>
  aiAdminRequest(`/connections/${id}`, { method: 'DELETE' }, 'delete AI connection');
export const testAiConnection = (id) =>
  aiAdminRequest(`/connections/${id}/test`, { method: 'POST' }, 'test AI connection');
export const fetchAiConnectionModels = (id) => aiAdminRequest(`/connections/${id}/models`, {}, 'fetch AI connection models');
export const fetchAiFeatures = () => aiAdminRequest('/features', {}, 'fetch AI features');
export const assignAiFeature = (code, connectionId) =>
  aiAdminRequest(`/features/${code}`, { method: 'PUT', body: JSON.stringify({ connection_id: connectionId }) }, 'assign AI feature');
export const fetchAiCalls = (limit = 100) => aiAdminRequest(`/calls?limit=${limit}`, {}, 'fetch AI calls');
export const fetchAiUsage = () => aiAdminRequest('/usage', {}, 'fetch AI usage');
